//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useMemo } from 'react';
import { jobsService, queryService } from '@/services';
import { type GetJobsOptions } from '@/services/apiTypes';
import { useApiErrorHandler } from './useApiErrorHandler';

/**
 * Jobs and Logs API surface.  These endpoints are stateless calls on the jobs
 * service (plus two query-service calls), so this hook subscribes to no React
 * context — consumers re-render only on their own state, not on unrelated global
 * state changes elsewhere in the app.
 */
export function useJobsActions() {
  const handleError = useApiErrorHandler();

  // `background` is forwarded so live-mode polls stay tagged (and don't reset
  // the inactivity timeout).  getJobs/getJobStats carry it inside their options
  // object; getJobStatus/getJobCounts take it as an explicit argument.
  const getJobStatus = useCallback((jobId: string, background = false) =>
    handleError('getJobStatus',
                () => jobsService.getJobStatus(jobId, background)),
    [handleError]);

  const getJobs = useCallback((options?: GetJobsOptions) =>
    handleError('getJobs', () => jobsService.getJobs(options)),
    [handleError]);

  const getJobCounts = useCallback(
    (background = false, includeActiveOthers = true) =>
    handleError('getJobCounts',
                () => jobsService.getJobCounts(background, includeActiveOthers)),
    [handleError]);

  const getJobStats = useCallback((options?: GetJobsOptions) =>
    handleError('getJobStats', () => jobsService.getJobStats(options)),
    [handleError]);

  const cancelJob = useCallback((jobId: string) =>
    handleError(`cancelJob ${jobId}`, () => jobsService.cancelJob(jobId)),
    [handleError]);

  const cancelJobs = useCallback((jobIds: (number | string)[]) =>
    handleError('cancelJobs', () => jobsService.cancelJobs(jobIds)),
    [handleError]);

  const setPriority = useCallback((jobId: string | number, priority: number) =>
    handleError(`setPriority ${jobId}`,
      () => jobsService.setPriority(jobId, priority)),
    [handleError]);

  const getAnswer = useCallback(
    (jobId: string, offset: number, length: number) =>
      handleError('getAnswer',
        () => queryService.getAnswer(jobId, offset, length)),
    [handleError]);

  const submitQuery = useCallback((query: string, dataset: string) =>
    handleError('submitQuery', () => queryService.submitQuery(query, dataset)),
    [handleError]);

  return useMemo(() => ({
    getJobStatus, getJobs, getJobCounts, getJobStats, cancelJob,
    cancelJobs, setPriority, getAnswer, submitQuery
  }), [getJobStatus, getJobs, getJobCounts, getJobStats, cancelJob,
       cancelJobs, setPriority, getAnswer, submitQuery]);
}
